import loomIcon from '../img/loom.svg';
import microsoftTeamsIcon from '../img/microsoft-teams.svg';
import googleDriveIcon from '../img/google-drive.svg';

export type SuggestionProductKey =
	| 'googleDocs'
	| 'googleSheets'
	| 'googleSlides'
	| 'googleDrive'
	| 'loom'
	| 'microsoftTeams'
	| 'link';

export enum Action {
	SUGGEST = 'SUGGEST',
	ACCEPT = 'ACCEPT',
	REJECT = 'REJECT',
	IGNORE = 'IGNORE',
}

export const EXTERNAL_PRODUCT_CONNECTION = {
	LOOM: 'loom',
	MICROSOFT_TEAMS: 'teams',
	GOOGLE_DRIVE: 'gdrive',
} as const;

export type ExternalProductConnection =
	(typeof EXTERNAL_PRODUCT_CONNECTION)[keyof typeof EXTERNAL_PRODUCT_CONNECTION];

export type AuthenticationStatus = {
	isAuthenticated: boolean;
	outboundAuthUrl?: string;
	error?: Error;
};

export const ALL_EXTERNAL_PRODUCT_CONNECTIONS: ExternalProductConnection[] = Object.values(
	EXTERNAL_PRODUCT_CONNECTION,
);

export const EXTERNAL_PRODUCT_CONNECTION_ICON_URLS: Record<ExternalProductConnection, string> = {
	[EXTERNAL_PRODUCT_CONNECTION.LOOM]: loomIcon,
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS]: microsoftTeamsIcon,
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: googleDriveIcon,
};

export type SocialSignal = {
	totalVisits?: number;
	lastVisited?: string; // ISO 8601 date string
	ownerIsSpaceContributor?: boolean;
};

export type Suggestion = {
	// ARI of a remote object, remote object is a 3P content
	id: string;
	title: string;
	description?: string;
	explanation?: string;
	socialSignal?: SocialSignal;
	url: string;
	parentId: number;
	action: Action;
	fallbackParents?: number[];
	iconUrl?: string;
	creatorAccountId?: string;
};

export type SuggestionGroup = {
	parentId: number;
	remoteObjects: Suggestion[];
};

export const PRODUCT_ICON_MAP: Array<{
	pattern: string;
	product: string;
	productKey: Exclude<SuggestionProductKey, 'link'>;
	iconUrl: string;
}> = [
	{
		pattern: 'docs.google.com/document',
		product: 'Google Docs',
		productKey: 'googleDocs',
		iconUrl: 'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_document_x32.png',
	},
	{
		pattern: 'docs.google.com/spreadsheets',
		product: 'Google Sheets',
		productKey: 'googleSheets',
		iconUrl: 'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_spreadsheet_x32.png',
	},
	{
		pattern: 'docs.google.com/presentation',
		product: 'Google Slides',
		productKey: 'googleSlides',
		iconUrl: 'https://ssl.gstatic.com/docs/presentations/images/favicon5.ico',
	},
	{
		pattern: 'drive.google.com',
		product: 'Google Drive',
		productKey: 'googleDrive',
		iconUrl: 'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_document_x32.png',
	},
	{
		pattern: 'loom.com',
		product: 'Loom',
		productKey: 'loom',
		iconUrl: 'https://cdn.loom.com/assets/favicons-loom/favicon.ico',
	},
	{
		pattern: 'teams.microsoft.com',
		product: 'Microsoft Teams',
		productKey: 'microsoftTeams',
		iconUrl: microsoftTeamsIcon,
	},
];

function getProductIconMapEntryForContentUrl(url: string) {
	return PRODUCT_ICON_MAP.find((entry) => url.includes(entry.pattern));
}

export function getSuggestionProductKeyFromContentUrl(url: string): SuggestionProductKey {
	return getProductIconMapEntryForContentUrl(url)?.productKey ?? 'link';
}

export function getSuggestionIconUrlFromContentUrl(url: string): string {
	return getProductIconMapEntryForContentUrl(url)?.iconUrl ?? '';
}
